import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    FiMail, FiArrowRight, FiArrowLeft, FiAlertCircle, FiCheckCircle,
} from 'react-icons/fi';
import { requestPasswordResetApi } from '../../Api';
import { validateEmail } from '../../validators';
import { notify } from '../../components/Notify';
import useCooldown, { formatarCooldown } from '../../hooks/useCooldown';

export default function EsqueciSenha() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [enviado, setEnviado] = useState(false);
    // Cooldown de 10min entre envios (mesma janela do backend). Persistido
    // em localStorage — sobrevive a refresh e impede spam.
    const cooldown = useCooldown('uen_reset_senha_cooldown', 10);

    async function handleSubmit(e) {
        e.preventDefault();
        if (loading || cooldown.isActive) return;

        const r = validateEmail(email);
        if (!r.valid) {
            setError(r.error);
            return;
        }

        setLoading(true);
        try {
            await requestPasswordResetApi({ email: email.trim() });
            // O backend sempre responde 200 (não revela se o email existe).
            // Mostramos confirmação genérica e disparamos o cooldown.
            cooldown.start();
            setEnviado(true);
        } catch (err) {
            console.error('Erro ao solicitar reset:', err);
            notify('Não foi possível concluir a solicitação. Tente novamente.', 'error');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.brand}>
                    <div style={styles.brandLogo}>U</div>
                    <div style={styles.brandText}>
                        <span style={styles.brandName}>UEN</span>
                        <span style={styles.brandTag}>Estudantil</span>
                    </div>
                </div>

                {!enviado ? (
                    <>
                        <h1 style={styles.title}>Esqueci minha senha</h1>
                        <p style={styles.subtitle}>
                            Informe o email cadastrado e enviaremos um link para você criar uma nova senha.
                        </p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="email">E-mail</label>
                                <div style={styles.inputWrap}>
                                    <span style={styles.inputIcon}><FiMail size={18} /></span>
                                    <input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="seu@email.com"
                                        style={{
                                            ...styles.input,
                                            ...(error ? styles.inputError : {}),
                                        }}
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (error) setError('');
                                        }}
                                    />
                                </div>
                                {error && (
                                    <div style={styles.fieldError}>
                                        <FiAlertCircle size={14} /> {error}
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || cooldown.isActive}
                                style={{ ...styles.submit, opacity: (loading || cooldown.isActive) ? 0.65 : 1 }}
                            >
                                {loading ? (
                                    <>
                                        <span style={styles.spinner} />
                                        Enviando...
                                    </>
                                ) : cooldown.isActive ? (
                                    <>Aguarde {formatarCooldown(cooldown.secondsLeft)} para reenviar</>
                                ) : (
                                    <>
                                        Enviar link de redefinição
                                        <FiArrowRight size={18} />
                                    </>
                                )}
                            </button>
                        </form>

                        <Link to="/recuperar-conta" style={{ ...styles.backLink, color: '#0EA5E9' }}>
                            Não tem email cadastrado? Recuperar conta com CPF →
                        </Link>
                        <br />
                        <Link to="/login" style={styles.backLink}>
                            <FiArrowLeft size={14} /> Voltar para o login
                        </Link>
                    </>
                ) : (
                    <div style={styles.successBox}>
                        <div style={styles.successIcon}>
                            <FiCheckCircle size={36} />
                        </div>
                        <h1 style={styles.title}>Verifique seu email</h1>
                        <p style={styles.subtitle}>
                            Se uma conta UEN existir com o email <strong>{email}</strong>,
                            você receberá um link para redefinir sua senha em alguns instantes.
                        </p>
                        <p style={{ ...styles.subtitle, fontSize: '0.85rem', marginTop: 8 }}>
                            O link é válido por 60 minutos. Não esqueça de conferir a caixa de spam.
                        </p>
                        <Link
                            to="/login"
                            style={{
                                ...styles.submit,
                                width: 'auto',
                                display: 'inline-flex',
                                padding: '12px 26px',
                                textDecoration: 'none',
                                marginTop: 16,
                            }}
                        >
                            <FiArrowLeft size={16} /> Voltar para o login
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 60%, #F0FDFA 100%)',
        display: 'grid',
        placeItems: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: '#0F172A',
    },
    card: {
        width: '100%',
        maxWidth: 440,
        background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15,23,42,0.04)',
        padding: '40px 36px',
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 32,
    },
    brandLogo: {
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center',
        fontWeight: 800, fontSize: 18,
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
    },
    brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandName: { fontWeight: 700, fontSize: '1.1rem', color: '#0F172A' },
    brandTag: { fontSize: '0.75rem', color: '#64748B', marginTop: 2 },

    title: {
        fontSize: '1.6rem', fontWeight: 700, color: '#0F172A',
        margin: '0 0 8px', letterSpacing: '-0.02em',
    },
    subtitle: {
        color: '#64748B', margin: '0 0 24px', fontSize: '0.92rem',
        lineHeight: 1.55,
    },

    field: { marginBottom: 20 },
    label: {
        display: 'block', fontSize: '0.78rem', fontWeight: 600,
        color: '#334155', marginBottom: 6,
    },
    inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: {
        position: 'absolute', left: 14, color: '#94A3B8', pointerEvents: 'none',
        display: 'flex', alignItems: 'center',
    },
    input: {
        width: '100%', padding: '12px 14px 12px 42px',
        border: '1.5px solid #E2E8F0', borderRadius: 10,
        fontSize: '0.95rem', color: '#0F172A', background: '#FFFFFF',
        outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    },
    inputError: { borderColor: '#F43F5E' },
    fieldError: {
        marginTop: 6, fontSize: '0.8rem', color: '#E11D48',
        display: 'flex', alignItems: 'center', gap: 4,
    },

    submit: {
        width: '100%', padding: '12px 18px',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff', border: 'none', borderRadius: 10,
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, fontFamily: 'inherit',
    },
    spinner: {
        width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        marginTop: 22, color: '#059669', fontSize: '0.88rem',
        fontWeight: 600, textDecoration: 'none',
    },
    successBox: { textAlign: 'center' },
    successIcon: {
        width: 72, height: 72, borderRadius: '50%',
        background: '#ECFDF5', color: '#059669',
        display: 'grid', placeItems: 'center',
        margin: '0 auto 18px',
    },
};
